<template>
  <div :class="`form-group ${error ? 'has-error' : ''}`" class="c-upload-widget">
    <label v-if="label" class="control-label">{{ label }}</label>

    <div v-if="(existingImages && existingImages.length) || existingImagesUrl">
      <c-button
        theme="primary"
        size="md"
        @click="openPopUp"
        :show-indicator="showIndicator"
      >Choose an image</c-button>
      <div class="c-upload-widget__popup" v-if="isOpenPopup" v-click-outside="closePopup">
        <div class="c-upload-popup-header">
          Upload Image:
          <input :id="id || 'c-uploader'" role="uploadcare-uploader" type="hidden" v-bind="$attrs" :data-input-accept-types="inputAcceptTypes" ref="input" />
        </div>
        <div>
          <div class="c-upload-popup-no-found" v-if="!existingImages.length">No images found.</div>

          <div class="c-upload-popup-list" v-else>
            <div
              v-for="(item, index) in existingImages"
              @click="setActive(item)"
              :key="index"
              class="c-upload-popup-item"
              :class="{ active: activeImage && activeImage.uuid && activeImage.uuid === item.uuid }"
            >
              <img :src="item.url" :alt="item.name" />
            </div>
          </div>
        </div>

        <div class="c-upload-popup-footer">
          <div></div>
          <c-button :disabled="!activeImage" @click="selectFromExisting">Select</c-button>
        </div>
      </div>
    </div>

    <div v-else>
      <c-button
        v-if="showIndicator"
        :show-indicator="true"
        theme="default"
        disabled
      >{{ buttonText || 'Choose a file' }}</c-button>

      <!-- v-show keeps the input in DOM so uploadcare.Widget ref stays valid -->
      <div v-show="!showIndicator">
        <input :id="id || 'c-uploader'" role="uploadcare-uploader" type="hidden" v-bind="$attrs" :data-input-accept-types="inputAcceptTypes" ref="input" />
      </div>
    </div>

    <span v-if="fileName">{{ fileName }}</span
    ><span v-else>&nbsp; </span>
    <small class="text-danger" v-if="error || validationError">
      {{ error || validationError }}
    </small>
    <span class="help-block" v-if="help">{{ help }}</span>
  </div>
</template>

<script>
import { fetchData } from "../../api/network";

export default {
  props: [
    "label",
    "buttonText",
    "onSuccess",
    "onError",
    "error",
    "help",
    "id",
    "theme",
    "showIndicator",
    "existingImages",
    "existingImagesUrl",
    "uuid",
    // Optional constraints. Left empty the widget behaves exactly as before.
    "allowedExtensions",
    "maxSizeMb",
  ],
  data() {
    return {
      fileName: "",
      activeImage: null,
      isOpenPopup: false,
      validationError: "",
    };
  },
  computed: {
    normalizedExtensions() {
      return (this.allowedExtensions || [])
        .map((ext) => String(ext).toLowerCase().replace(/^\./, ""))
        .filter(Boolean);
    },

    // Uploadcare builds its own <input type="file"> and takes `accept` from this
    // option - putting `accept` on our hidden input would do nothing.
    inputAcceptTypes() {
      if (!this.normalizedExtensions.length) return undefined;

      return this.normalizedExtensions.map((ext) => `.${ext}`).join(",");
    },

    maxSizeBytes() {
      return this.maxSizeMb ? Number(this.maxSizeMb) * 1024 * 1024 : null;
    },

    constraintsHint() {
      const parts = [];

      if (this.normalizedExtensions.length) {
        parts.push(`Allowed formats: ${this.normalizedExtensions.map((ext) => `.${ext}`).join(", ")}.`);
      }

      if (this.maxSizeMb) {
        parts.push(`Maximum size: ${this.maxSizeMb} MB.`);
      }

      return parts.join(" ");
    },
  },
  mounted() {
    if (!this.existingImages && !this.existingImagesUrl) {
      this.initWidget();
    }
  },
  methods: {
    /**
     * The popup markup is behind v-if, so every open produces a brand new input
     * element and therefore a brand new widget - guard on the element itself
     * rather than on a boolean, or validators get attached twice.
     */
    initWidget() {
      const input = this.$refs.input;

      if (!input || this._widgetEl === input) return;

      this._widgetEl = input;

      const self = this;
      const widget = uploadcare.Widget(input);

      this.buildValidators().forEach((validator) => widget.validators.push(validator));

      widget.onChange((file) => {
        if (file) {
          file.done((fileInfo) => {
            self.validationError = "";
            self.fileName = fileInfo.name;
            self.$emit("onSuccess", fileInfo);

            widget.value(null);
          });
        }
      });
    },

    /**
     * Uploadcare validators receive a fileInfo whose fields fill in gradually,
     * so each check has to tolerate nulls. Extension is what the UI promises the
     * user; the real mime type is verified server-side.
     */
    buildValidators() {
      const self = this;
      const validators = [];
      const extensions = this.normalizedExtensions;
      const maxSizeBytes = this.maxSizeBytes;

      if (extensions.length) {
        validators.push((fileInfo) => {
          const name = fileInfo.name ? String(fileInfo.name).toLowerCase() : "";
          const extension = name.includes(".") ? name.split(".").pop() : "";

          if (extension && !extensions.includes(extension)) {
            self.validationError = self.constraintsHint;
            throw new Error("fileFormat");
          }
        });
      }

      if (maxSizeBytes) {
        validators.push((fileInfo) => {
          if (fileInfo.size !== null && fileInfo.size > maxSizeBytes) {
            self.validationError = self.constraintsHint;
            throw new Error("fileMaximumSize");
          }
        });
      }

      // Uploadcare's own plan cap applies to every upload regardless of the props
      // above; without this an oversized file fails mid-way with a bare "Can't upload".
      if (typeof window.uploadcareUploadLimitValidator === "function") {
        validators.push(window.uploadcareUploadLimitValidator);
      }

      return validators;
    },

    selectFromExisting() {
      this.validationError = "";
      this.$emit("onSuccess", this.activeImage);
      this.isOpenPopup = false;
    },
    setActive(item) {
      this.activeImage = item;
    },
    async openPopUp() {
      if (this.existingImagesUrl) {
        const res = await fetchData(this.existingImagesUrl);
        this.existingImages = res.data.uploads;
      }

      this.isOpenPopup = true;

      this.$nextTick(() => {
        this.initWidget();
      });

      this.$emit("open-popup");
    },
    closePopup() {
      this.isOpenPopup = false;
      this._widgetEl = null;
    },
  },
};
</script>

<style lang="scss">
.c-upload-popup-header {
  display: flex;
  grid-gap: 5px;
  align-items: center;
  //margin-bottom: 20px;

  position: sticky;
  top: 0;
  background: white;
  width: 100%;
  padding: 8px 15px;
  border-bottom: 1px solid #d7d7d7;
}

.c-upload-popup-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  //margin-top: 20px;

  position: sticky;
  bottom: 0;
  background: white;
  width: 100%;
  padding: 8px 15px;
  border-top: 1px solid #d7d7d7;
}

.c-upload-popup-item {
  padding: 2px;
  border-radius: 3px;
  cursor: pointer;
  width: 95px;
  height: 95px;
  display: flex;
  justify-content: center;
  align-items: center;

  &.active {
    border: 1px solid rgba(27, 217, 210, 1);
    background-color: rgba(27, 217, 210, 0.2);
  }
}

.c-upload-popup-list {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 0;
  padding: 5px;

  img {
    width: 100%;
    max-width: 100%;
    height: auto;
    object-position: center;
    max-height: 100%;
    object-fit: contain;
  }
}

.c-upload-popup-no-found {
  font-size: 16px;
  padding: 20px;
}

.c-upload-widget {
  position: relative;
  width: 100%;

  &__popup {
    position: absolute;
    border-radius: 3px;
    min-width: 350px;
    max-width: 500px;
    width: 100%;
    height: 435px;
    overflow: auto;
    left: 0;
    z-index: 999;

    top: 100%;
    border: 1px solid #d7d7d7;
    background-color: white;

    display: grid;
    grid-template-rows: auto 1fr auto;
  }
}
</style>